import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, CheckCircle2, Circle, LogIn } from "lucide-react";
import { api } from "../../lib/api";
import { apiError } from "../../lib/auth";
import { NeonButton } from "../common";
import { Panel, ago } from "./util";

const Md = ({ text }) => (
  <div className="space-y-1.5 text-sm text-white/80" data-testid="brief-text">
    {text.split("\n").map((l, i) => {
      const t = l.trim();
      if (!t) return null;
      if (t.startsWith("#")) return <p key={i} className="font-bold text-brand pt-3 font-mono text-xs tracking-widest uppercase">{t.replace(/^#+\s*/, "")}</p>;
      return <p key={i} className={/^([-*]|\d+\.)\s/.test(t) ? "pl-3" : ""}>{t.replace(/\*\*/g, "")}</p>;
    })}
  </div>
);

export const Brief = ({ userId, days }) => {
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/insights/brief", { params: { user_id: userId } }).then((r) => setB(r.data)); }, [userId]);
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/insights/analyze", null, { params: { user_id: userId, days } }); setB(data); toast.success("MARI finished the workflow analysis"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Panel title="MARI workflow analysis" testid="insights-brief"
      right={<NeonButton onClick={run} disabled={busy} data-testid="brief-generate-btn"><Sparkles className="w-3 h-3" /> {busy ? "Analyzing…" : b?.text ? "Re-analyze" : "Analyze workflow"}</NeonButton>}>
      {b?.text ? <><p className="font-mono text-[10px] text-white/40 mb-2">generated {ago(b.generated_at)} · from real usage only</p><Md text={b.text} /></>
        : <p className="text-sm text-white/45">MARI reads his sessions, friction signals and questions, then writes concrete workflow fixes. Nothing is invented.</p>}
    </Panel>
  );
};

export const Workflow = ({ training, logins, mari }) => (
  <Panel title={`Pilot workflow · ${training.completed}/${training.total}`} testid="insights-workflow">
    <ol className="space-y-2">
      {training.steps.map((s, i) => (
        <li key={s.id} className="flex gap-2 text-sm" data-testid={`workflow-step-${s.id}`}>
          {s.done ? <CheckCircle2 className="w-4 h-4 text-brand shrink-0 mt-0.5" /> : <Circle className={`w-4 h-4 shrink-0 mt-0.5 ${training.next?.id === s.id ? "text-amber-300 animate-pulse" : "text-white/25"}`} />}
          <span className={s.done ? "text-white/80" : "text-white/50"}>{i + 1}. {s.title}</span>
        </li>
      ))}
    </ol>
    <p className="label-xs mt-6 mb-2">Logins</p>
    <ul className="font-mono text-[11px] space-y-1 max-h-32 overflow-y-auto" data-testid="insights-logins">
      {!logins.length && <li className="text-white/40">none recorded yet</li>}
      {logins.map((l, i) => <li key={i} className="flex gap-2"><LogIn className="w-3 h-3 text-accent2 mt-0.5" /><span className="text-white/70">{ago(l.at)}</span><span className="text-white/40 uppercase">{l.method}</span><span className="text-white/30 truncate">{l.user_agent?.match(/\(([^)]+)\)/)?.[1]}</span></li>)}
    </ul>
    <p className="label-xs mt-6 mb-2">Questions asked to MARI</p>
    <ul className="text-xs space-y-1.5 max-h-40 overflow-y-auto" data-testid="insights-mari-questions">
      {!mari.length && <li className="text-white/40">none yet</li>}
      {mari.map((q, i) => <li key={i} className="border-l border-amber-300/50 pl-2 text-white/75">{q.text}<span className="block font-mono text-[10px] text-white/35">{ago(q.ts)}</span></li>)}
    </ul>
  </Panel>
);

export const Actions = ({ items }) => (
  <Panel title="Actions he took (audit trail)" testid="insights-actions">
    <ul className="font-mono text-[11px] divide-y divide-white/5 max-h-72 overflow-y-auto">
      {!items.length && <li className="text-white/40 py-1">no recorded actions yet</li>}
      {items.map((a, i) => <li key={i} className="py-1.5 flex gap-2"><span className="text-brand truncate">{a.action}</span><span className="text-white/40 truncate">{a.entity_id}</span><span className="text-white/30 ml-auto shrink-0">{ago(a.created_at || a.ts)}</span></li>)}
    </ul>
  </Panel>
);
